let enabled = false;
let generation = 0;
const listeners = new Set();

function notify() {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // A presentation subscriber cannot prevent the process-wide gate from
      // closing or starve other subscribers of the same safety boundary.
    }
  }
}

/**
 * Publishes an authoritative public-config result into the process-local gate.
 * Only an explicit true opens the gate; every other value is fail-closed.
 */
export function publishGoogleIntegrationsRuntimeState(value) {
  const nextEnabled = value === true;
  if (nextEnabled !== enabled) {
    enabled = nextEnabled;
    generation += 1;
    notify();
  }
  return generation;
}

/**
 * Starts a freshness boundary synchronously. Advancing the generation even
 * while already OFF prevents work from an earlier ON window becoming valid
 * again after a fast OFF -> ON refresh.
 */
export function closeGoogleIntegrationsRuntimeBoundary() {
  enabled = false;
  generation += 1;
  notify();
  return generation;
}

export function captureGoogleIntegrationsRuntimeGeneration() {
  return enabled ? generation : null;
}

export function isGoogleIntegrationsRuntimeGenerationCurrent(value) {
  return Number.isSafeInteger(value) && enabled && value === generation;
}

export function onGoogleIntegrationsRuntimeBoundary(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export class GoogleIntegrationsRuntimeSupersededError extends Error {
  constructor() {
    super("google_integrations_runtime_superseded");
    this.name = "GoogleIntegrationsRuntimeSupersededError";
  }
}

/**
 * Guards both sides of an async operation. This is used by query functions so
 * a late response cannot repopulate React Query after the kill switch closes.
 */
export async function runWithGoogleIntegrationsRuntimeGeneration(value, operation) {
  if (!isGoogleIntegrationsRuntimeGenerationCurrent(value)) {
    throw new GoogleIntegrationsRuntimeSupersededError();
  }
  let result;
  try {
    result = await operation();
  } catch (error) {
    if (!isGoogleIntegrationsRuntimeGenerationCurrent(value)) {
      throw new GoogleIntegrationsRuntimeSupersededError();
    }
    throw error;
  }
  if (!isGoogleIntegrationsRuntimeGenerationCurrent(value)) {
    throw new GoogleIntegrationsRuntimeSupersededError();
  }
  return result;
}
